import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * SupportersHeatmap — Leaflet map with a per-bucket pin (circleMarker) overlay.
 * Each point is a ~150m grid bucket produced by /api/stats/supporters-heatmap
 * (coordinates rounded server-side to protect individual privacy).
 *
 * Props:
 *   points: Array<{ lat, lng, count }>
 *   height: CSS height (default 480)
 */
export const SupportersHeatmap = ({ points = [], height = 480 }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      zoomControl: true,
      scrollWheelZoom: true,
    }).setView([-14.235, -51.9253], 4); // Brazil center
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }
    if (!points.length) return;

    // Radius + color scale by count. We use log so a spot with 100 supporters
    // is not 100× larger than one with 1 — visual perception is compressed.
    const maxCount = points.reduce((m, p) => Math.max(m, p.count || 1), 1);
    const colorFor = (count) => {
      const ratio = Math.log(count + 1) / Math.log(maxCount + 1);
      if (ratio > 0.75) return '#EF4444'; // red — hotspot
      if (ratio > 0.5) return '#F59E0B';  // amber
      if (ratio > 0.25) return '#22C55E'; // green
      return '#2563EB';                   // blue — sparse
    };
    const radiusFor = (count) => {
      const ratio = Math.log(count + 1) / Math.log(maxCount + 1);
      return 5 + ratio * 12; // 5px .. 17px
    };

    const group = L.layerGroup();
    points.forEach((p) => {
      const count = p.count || 1;
      const marker = L.circleMarker([p.lat, p.lng], {
        radius: radiusFor(count),
        color: '#0F172A',
        weight: 1,
        fillColor: colorFor(count),
        fillOpacity: 0.75,
      });
      const label = count === 1 ? '1 apoiador' : `${count} apoiadores`;
      marker.bindTooltip(label, {
        direction: 'top',
        offset: [0, -6],
        className: 'text-xs',
      });
      group.addLayer(marker);
    });
    group.addTo(map);
    layerRef.current = group;

    // Fit bounds
    try {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 13 });
    } catch {
      /* ignore */
    }
  }, [points]);

  return (
    <div
      ref={containerRef}
      className="w-full rounded-xl overflow-hidden border border-slate-200"
      style={{ height }}
      data-testid="supporters-heatmap"
    />
  );
};
